import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Plus, Upload, Check } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_TONE = {
  low:      "bg-slate-50 text-slate-700 border-slate-200",
  moderate: "bg-amber-50 text-amber-800 border-amber-200",
  high:     "bg-orange-50 text-orange-800 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

const RESOLUTION_VALUES = ["open", "under_review", "corrected", "verified", "closed"];

const CATEGORIES = [
  "institutional_governance",
  "faculty_qualifications",
  "curriculum_quality",
  "learning_outcomes",
  "student_records_management",
  "assessment_and_evaluation",
  "ethics_and_compliance",
  "operational_sustainability",
];

export default function CompliancePanel({ applicationId, siteVisits = [] }) {
  const { user } = useAuth();
  const isStaff = user.role === "admin" || user.role === "reviewer";
  const [findings, setFindings] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    site_visit_id: "",
    category: "institutional_governance",
    title: "",
    severity: "moderate",
    description: "",
    corrective_action_required: "",
    due_date: "",
  });
  const [selected, setSelected] = useState(null);
  const [evidenceText, setEvidenceText] = useState("");
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/compliance-findings", {
        params: { school_application_id: applicationId },
      });
      setFindings(r.data);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId]);

  const create = async () => {
    if (!form.site_visit_id || !form.title) {
      toast.error("Pick a site visit and add a finding title");
      return;
    }
    try {
      await api.post("/compliance-findings", form);
      toast.success("Finding recorded");
      setShowNew(false);
      setForm({ ...form, title: "", description: "", corrective_action_required: "", due_date: "" });
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const patch = async (id, payload) => {
    try {
      const r = await api.patch(`/compliance-findings/${id}`, payload);
      toast.success("Finding updated");
      setSelected(r.data);
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const uploadEvidence = async () => {
    if (!selected || !evidenceText.trim()) return;
    setUploading(true);
    try {
      await api.patch(`/compliance-findings/${selected.id}`, {
        resolution_evidence: evidenceText,
      });
      toast.success("Evidence saved");
      setEvidenceText("");
      reload();
      const r = await api.get("/compliance-findings", {
        params: { school_application_id: applicationId },
      });
      const updated = r.data.find((f) => f.id === selected.id);
      if (updated) setSelected(updated);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="compliance-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-primary flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> Compliance findings ({findings.length})
        </h3>
        {isStaff && (
          <Button size="sm" variant="outline" onClick={() => setShowNew(true)} data-testid="new-finding-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Record finding
          </Button>
        )}
      </div>

      {findings.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 border-slate-200 text-sm" data-testid="cf-empty">
          No compliance findings yet.
        </Card>
      ) : (
        <div className="space-y-2">
          {findings.map((f) => (
            <Card key={f.id} className="p-4 border-slate-200" data-testid={`cf-row-${f.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge className={`border capitalize text-[10px] ${SEVERITY_TONE[f.severity]}`}>
                      {f.severity}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {f.resolution_status?.replace(/_/g, " ")}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] capitalize bg-slate-50">
                      {f.category?.replace(/_/g, " ")}
                    </Badge>
                  </div>
                  <div className="text-sm font-semibold text-primary">{f.title}</div>
                  {f.description && <p className="text-xs text-slate-600 mt-1">{f.description}</p>}
                  {f.corrective_action_required && (
                    <p className="text-xs text-slate-700 mt-1">
                      <strong>CAP:</strong> {f.corrective_action_required}
                      {f.due_date && <span className="text-slate-500"> · due {f.due_date}</span>}
                    </p>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => { setSelected(f); setEvidenceText(f.resolution_evidence || ""); }} data-testid={`cf-view-${f.id}`}>
                  Open
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New finding dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="cf-new-dialog">
          <DialogHeader><DialogTitle>Record compliance finding</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Site visit</Label>
              <Select value={form.site_visit_id} onValueChange={(v) => setForm({ ...form, site_visit_id: v })}>
                <SelectTrigger data-testid="cf-sv-select"><SelectValue placeholder="Pick a site visit" /></SelectTrigger>
                <SelectContent>
                  {siteVisits.length === 0 && (
                    <SelectItem value="__none__" disabled>(No site visits yet — schedule one first)</SelectItem>
                  )}
                  {siteVisits.map((sv) => (
                    <SelectItem key={sv.id} value={sv.id}>
                      {sv.scheduled_date || sv.visit_date} · {sv.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="cf-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="cf-sev"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["low", "moderate", "high", "critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input data-testid="cf-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} data-testid="cf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Corrective action required</Label>
              <Textarea rows={2} data-testid="cf-cap" value={form.corrective_action_required} onChange={(e) => setForm({ ...form, corrective_action_required: e.target.value })} />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" data-testid="cf-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={create} className="bg-primary text-white hover:bg-[#113A64]" data-testid="cf-submit">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / response dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="cf-detail-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.title}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Category</span><div className="text-slate-700 capitalize">{selected.category?.replace(/_/g, " ")}</div></div>
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Severity</span><div><Badge className={`border ${SEVERITY_TONE[selected.severity]}`}>{selected.severity}</Badge></div></div>
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Due</span><div className="text-slate-700">{selected.due_date || "—"}</div></div>
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Status</span><div><Badge variant="outline" className="capitalize">{selected.resolution_status?.replace(/_/g, " ")}</Badge></div></div>
              </div>
              {selected.description && (
                <div className="mb-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">Description</div>
                  <p className="text-sm text-slate-700">{selected.description}</p>
                </div>
              )}
              {selected.corrective_action_required && (
                <div className="mb-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">Corrective action required</div>
                  <p className="text-sm text-slate-700">{selected.corrective_action_required}</p>
                </div>
              )}

              {/* Institution response / supporting documentation */}
              <div className="mb-3 border-t pt-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1 flex items-center gap-1">
                  <Upload className="h-3 w-3" /> Institution response &amp; evidence
                </div>
                <Textarea
                  rows={3}
                  placeholder="Describe corrective actions taken and reference any supporting documentation (e.g. /dashboard/documents URLs)."
                  data-testid="cf-evidence-text"
                  value={evidenceText}
                  onChange={(e) => setEvidenceText(e.target.value)}
                  className="text-xs"
                />
                <div className="text-right mt-1">
                  <Button size="sm" onClick={uploadEvidence} disabled={uploading || !evidenceText.trim()} data-testid="cf-upload-evidence">
                    {uploading ? "Saving…" : "Save response"}
                  </Button>
                </div>
              </div>

              {isStaff && (
                <div className="border-t pt-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">Transition status</div>
                  <div className="flex flex-wrap gap-1">
                    {RESOLUTION_VALUES.filter((v) => v !== selected.resolution_status).map((v) => (
                      <Button
                        key={v}
                        size="sm"
                        variant="outline"
                        onClick={() => patch(selected.id, { resolution_status: v })}
                        data-testid={`cf-transition-${v}`}
                        className="text-xs capitalize"
                      >
                        <Check className="h-3 w-3 mr-1" /> {v.replace(/_/g, " ")}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
